import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/lib/api';
import type { Envelope } from '@/lib/types';

/**
 * The unread-notifications badge on the bell, kept fresh by polling — the same
 * real-time model as unread messages. Only runs while signed in; App starts it on
 * authentication and stops it on sign-out. Polling pauses while the tab is hidden.
 */
const POLL_MS = 20_000;

export const useNotificationsStore = defineStore('notifications', () => {
  const unread = ref(0);
  let timer: number | null = null;

  async function refresh(): Promise<void> {
    try {
      unread.value = (await api.get<Envelope<{ unread: number }>>('/notifications/unread-count')).data.unread;
    } catch {
      // A failed poll (offline, 401 mid-logout) leaves the last known count.
    }
  }

  function start(): void {
    if (timer != null) return;
    void refresh();
    timer = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, POLL_MS);
  }

  function stop(): void {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
    unread.value = 0;
  }

  return { unread, refresh, start, stop };
});
